import { JPBReferenceProperty, JPBAssumptions, DEFAULT_ASSUMPTIONS, AreaGrouping, YearGrouping, AreaUnit, PriceMode, StatisticsMethod, SoilQualityGroup, SOIL_QUALITY_GROUPS, SOIL_INDEX, AverageCalculationMethod } from "./JPBState";
import { SettingsManager, Localization } from "@viamap/viamap2-common";

export function formatCurrency(value: number): string {
   if (value === 0 || !value) return "-";
   return new Intl.NumberFormat(Localization.getLocale(), {
      style: "currency",
      currency: "DKK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
      useGrouping: true
   }).format(value);
}

export function formatPricePerSquareMeter(value: number, unit: AreaUnit = AreaUnit.SquareMeter, forceFractionDigits?: number, currencyShow: boolean = true): string {
   if (!value || isNaN(value)) return "-";

   const displayValue = unit === AreaUnit.Hectare ? value * 10000 : value;
   const rounded = unit === AreaUnit.Hectare ? Math.round(displayValue) : Math.round(displayValue * 100) / 100;
   let fractionDigits = unit === AreaUnit.Hectare ? 0 : 2;
   if (forceFractionDigits !== undefined) {
      fractionDigits = forceFractionDigits;
   }

   const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true
   };

   if (currencyShow) {
      options.style = "currency";
      options.currency = "DKK";
   }

   return new Intl.NumberFormat(Localization.getLocale(), options).format(rounded);
}

export function formatArea(value: number, unit: AreaUnit = AreaUnit.SquareMeter, forceFractionDigits?: number): string {
   if (value === 0 || !value) return "-";

   const displayValue = unit === AreaUnit.Hectare ? value / 10000 : value;
   let fractionDigits = unit === AreaUnit.Hectare ? 4 : 0;
   if (forceFractionDigits !== undefined) {
      fractionDigits = forceFractionDigits;
   }

   return new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true
   }).format(displayValue);
}

export function formatDate(dateString: Date | string | null): string {
   if (!dateString) return "-";
   const datoFormatter = new Intl.DateTimeFormat(Localization.getLocale(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
   })
   return datoFormatter.format(new Date(dateString || 0));
}

export function formatWholeNumber(value: number): string {
   if (value === 0 || !value) return "-";
   return new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
   }).format(value);
}

export function formatPercentage(value: number | null | undefined): string {
   if (value === null || value === undefined || isNaN(value)) return "-";
   return new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      style: "percent"
   }).format(value / 100);
}


export type JPBStatisticsSeries = "indexed" | "effectiveMark" | "original";

type JPBSoilFactorsByGroup = { [soilGroup in SoilQualityGroup]: number };

function isValidPositiveNumber(value: unknown): value is number {
   return typeof value === "number" && isFinite(value) && value > 0;
}

function isValidNonNegativeNumber(value: unknown): value is number {
   return typeof value === "number" && isFinite(value) && value >= 0;
}

export type JPBWeightedValue = { value: number; weight: number };

function toFinitePositiveNumber(value: unknown, fallback: number): number {
   return typeof value === "number" && isFinite(value) && value > 0 ? value : fallback;
}

function toFiniteNonNegativeNumber(value: unknown, fallback: number): number {
   return typeof value === "number" && isFinite(value) && value >= 0 ? value : fallback;
}

function getWeightForAverageMode(
   averageCalculationMethod: AverageCalculationMethod,
   weightedArea: number
): number {
   if (averageCalculationMethod === AverageCalculationMethod.EqualPerSample) {
      return 1;
   }
   return weightedArea;
}

export function getSoilQualityFactorsByGroup(assumptions?: JPBAssumptions): JPBSoilFactorsByGroup {
   const source = assumptions ?? getResolvedJPBAssumptions();
   const configured = source.soilQualityFactors ?? DEFAULT_ASSUMPTIONS.soilQualityFactors;
   return {
      [SoilQualityGroup.JB_1_2]: toFiniteNonNegativeNumber(configured.JB_1_2, SOIL_INDEX[SoilQualityGroup.JB_1_2]),
      [SoilQualityGroup.JB_3_4]: toFiniteNonNegativeNumber(configured.JB_3_4, SOIL_INDEX[SoilQualityGroup.JB_3_4]),
      [SoilQualityGroup.JB_5_6]: toFiniteNonNegativeNumber(configured.JB_5_6, SOIL_INDEX[SoilQualityGroup.JB_5_6]),
      [SoilQualityGroup.JB_7_10]: toFiniteNonNegativeNumber(configured.JB_7_10, SOIL_INDEX[SoilQualityGroup.JB_7_10]),
      [SoilQualityGroup.JB_11]: toFiniteNonNegativeNumber(configured.JB_11, SOIL_INDEX[SoilQualityGroup.JB_11]),
   };
}

export function getResolvedJPBAssumptions(customAssumptions?: Partial<JPBAssumptions>  ): JPBAssumptions {
   const systemAssumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS);
   const merged = {
      ...DEFAULT_ASSUMPTIONS,
      ...systemAssumptions,
      ...customAssumptions,
      soilQualityFactors: {
         ...DEFAULT_ASSUMPTIONS.soilQualityFactors,
         ...systemAssumptions?.soilQualityFactors,
         ...customAssumptions?.soilQualityFactors
      }
   } as JPBAssumptions;

   merged.agricultureFactor = toFiniteNonNegativeNumber(
      merged.agricultureFactor,
      DEFAULT_ASSUMPTIONS.agricultureFactor
   );
   merged.nonAgricultureFactor = toFiniteNonNegativeNumber(
      merged.nonAgricultureFactor,
      DEFAULT_ASSUMPTIONS.nonAgricultureFactor
   );
   merged.soilQualityFactors = {
      JB_1_2: toFiniteNonNegativeNumber(merged.soilQualityFactors?.JB_1_2, DEFAULT_ASSUMPTIONS.soilQualityFactors.JB_1_2),
      JB_3_4: toFiniteNonNegativeNumber(merged.soilQualityFactors?.JB_3_4, DEFAULT_ASSUMPTIONS.soilQualityFactors.JB_3_4),
      JB_5_6: toFiniteNonNegativeNumber(merged.soilQualityFactors?.JB_5_6, DEFAULT_ASSUMPTIONS.soilQualityFactors.JB_5_6),
      JB_7_10: toFiniteNonNegativeNumber(merged.soilQualityFactors?.JB_7_10, DEFAULT_ASSUMPTIONS.soilQualityFactors.JB_7_10),
      JB_11: toFiniteNonNegativeNumber(merged.soilQualityFactors?.JB_11, DEFAULT_ASSUMPTIONS.soilQualityFactors.JB_11),
   };
   const buildingAreaOkHa = toFiniteNonNegativeNumber(merged.buildingAreaOkM2, DEFAULT_ASSUMPTIONS.buildingAreaOkM2);
   merged.buildingAreaOkM2 = buildingAreaOkHa * 10000;
   return merged;
}

export function getNonAgricultureToAgricultureRatio(assumptions?: JPBAssumptions): number {
   const source = assumptions ?? getResolvedJPBAssumptions();
   const agricultureFactor = toFinitePositiveNumber(source.agricultureFactor, DEFAULT_ASSUMPTIONS.agricultureFactor);
   const nonAgricultureFactor = toFiniteNonNegativeNumber(source.nonAgricultureFactor, DEFAULT_ASSUMPTIONS.nonAgricultureFactor);
   return nonAgricultureFactor / agricultureFactor;
}

function getEffectiveMarkDenominator(markOverlapPct: number, delta: number): number {
   const alpha = markOverlapPct / 100;
   return alpha + (1 - alpha) * delta;
}

export function getWeightedValuesForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): JPBWeightedValue[] {
   if (!properties || properties.length === 0) return [];

   const delta = opts?.delta ?? getNonAgricultureToAgricultureRatio();
   const averageCalculationMethod = opts?.averageCalculationMethod ?? AverageCalculationMethod.WeightedByArea;
   const out: JPBWeightedValue[] = [];

   for (const p of properties) {
      if (!isValidPositiveNumber(p.area)) continue;

      if (series === "indexed") {
         if (isValidPositiveNumber(p.pricePerSquareMeter)) {
            out.push({
               value: p.pricePerSquareMeter,
               weight: getWeightForAverageMode(averageCalculationMethod, p.area)
            });
         }
         continue;
      }

      if (series === "original") {
         if (isValidPositiveNumber(p.originalPricePerSquareMeter)) {
            out.push({
               value: p.originalPricePerSquareMeter,
               weight: getWeightForAverageMode(averageCalculationMethod, p.area)
            });
         }
         continue;
      }

      const markOverlap = p.overlapArea?.markOverlap;
      if (!isValidNonNegativeNumber(markOverlap)) continue;
      if (!isValidPositiveNumber(p.pricePerSquareMeter)) continue;

      const denom = getEffectiveMarkDenominator(markOverlap, delta);
      if (!isValidPositiveNumber(denom)) continue;

      const effectivePrice = p.pricePerSquareMeter / denom;
      const effectiveWeight = getWeightForAverageMode(averageCalculationMethod, p.area * denom);

      if (isValidPositiveNumber(effectivePrice) && isValidPositiveNumber(effectiveWeight)) {
         out.push({ value: effectivePrice, weight: effectiveWeight });
      }
   }

   return out;
}

export function calculateWeightedMean(values: JPBWeightedValue[]): number | null {
   if (!values || values.length === 0) return null;
   let weightedSum = 0;
   let totalWeight = 0;
   for (const v of values) {
      if (!isValidPositiveNumber(v.value) || !isValidPositiveNumber(v.weight)) continue;
      weightedSum += v.value * v.weight;
      totalWeight += v.weight;
   }
   if (totalWeight <= 0) return null;
   return weightedSum / totalWeight;
}

export function calculateWeightedMeanForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): number | null {
   return calculateWeightedMean(getWeightedValuesForSeries(properties, series, opts));
}

function getWeightedQuantile(sorted: JPBWeightedValue[], quantile: number): number | null {
   if (!sorted || sorted.length === 0) return null;
   const totalWeight = sorted.reduce((sum, d) => sum + d.weight, 0);
   if (totalWeight <= 0) return null;

   const target = totalWeight * quantile;
   let cumulative = 0;
   for (const d of sorted) {
      cumulative += d.weight;
      if (cumulative >= target) return d.value;
   }
   return sorted[sorted.length - 1]?.value ?? null;
}

export function calculateWeightedStatisticsForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   method: StatisticsMethod,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): { lower: number; middle: number; upper: number } | null {
   const weightedValues = getWeightedValuesForSeries(properties, series, opts);
   if (weightedValues.length === 0) return null;

   const sorted = [...weightedValues].sort((a, b) => a.value - b.value);
   const mean = calculateWeightedMean(sorted);
   if (mean === null) return null;

   if (method === "quantile") {
      const q25 = getWeightedQuantile(sorted, 0.25);
      const q75 = getWeightedQuantile(sorted, 0.75);
      return {
         lower: q25 ?? mean,
         middle: mean,
         upper: q75 ?? mean,
      };
   }

   if (method === "mean") {
      let lowerSum = 0;
      let lowerWeight = 0;
      let upperSum = 0;
      let upperWeight = 0;

      for (const d of sorted) {
         if (d.value <= mean) {
            lowerSum += d.value * d.weight;
            lowerWeight += d.weight;
         }
         if (d.value >= mean) {
            upperSum += d.value * d.weight;
            upperWeight += d.weight;
         }
      }

      const lowerAvg = lowerWeight > 0 ? lowerSum / lowerWeight : mean;
      const upperAvg = upperWeight > 0 ? upperSum / upperWeight : mean;

      return { lower: lowerAvg, middle: mean, upper: upperAvg };
   }

   const totalWeight = sorted.reduce((sum, d) => sum + d.weight, 0);
   if (totalWeight <= 0) return { lower: mean, middle: mean, upper: mean };
   const variance =
      sorted.reduce((sum, d) => sum + d.weight * Math.pow(d.value - mean, 2), 0) / totalWeight;
   const stdDev = Math.sqrt(variance);
   const margin = 0.674 * stdDev;

   return { lower: mean - margin, middle: mean, upper: mean + margin };
}

export function getSortedValuesForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): number[] {
   if (!properties || properties.length === 0) return [];

   return getWeightedValuesForSeries(properties, series, opts)
      .map(v => v.value)
      .sort((a, b) => a - b);
}

export function getStatisticsLabels(method: StatisticsMethod): { lower: string; middle: string; upper: string } {
   if (method === "quantile") {
      return {
         lower: "25%",
         middle: Localization.getText("Mean"),
         upper: "75%",
      };
   }

   if (method === "mean") {
      return {
         lower: Localization.getText("Below mean avg"),
         middle: Localization.getText("Mean"),
         upper: Localization.getText("Above mean avg"),
      };
   }

   return {
      lower: Localization.getText("Lower"),
      middle: Localization.getText("Mean"),
      upper: Localization.getText("Upper"),
   };
}

export function calculateEffectiveMarkSquareMeterPrice(
   property: JPBReferenceProperty,
   delta?: number
): number | null {
   const resolvedDelta = delta ?? getNonAgricultureToAgricultureRatio();
   const markOverlap = property.overlapArea?.markOverlap;
   if (!isValidNonNegativeNumber(markOverlap)) return null;
   if (!isValidPositiveNumber(property.pricePerSquareMeter)) return null;

   const denom = getEffectiveMarkDenominator(markOverlap, resolvedDelta);
   if (!isValidPositiveNumber(denom)) return null;

   return property.pricePerSquareMeter / denom;
}

export function mapSoilTypeToGroup(jordtype: string | null | undefined): SoilQualityGroup | null {
   if (!jordtype) return null;

   const normalized = jordtype.toLowerCase().trim();

   if (normalized.includes('sandblandet lerjord')) {
      return SoilQualityGroup.JB_5_6;
   }

   if (normalized.includes('lerblandet sandjord')) {
      return SoilQualityGroup.JB_3_4;
   }

   if (normalized.includes('grovsandet jord') || normalized.includes('finsandet jord')) {
      return SoilQualityGroup.JB_1_2;
   }

   if (
      (normalized.includes('lerjord') ||
         normalized.includes('svær lerjord') ||
         normalized.includes('meget svær lerjord')) &&
      !normalized.includes('sandblandet') &&
      !normalized.includes('lerblandet')
   ) {
      return SoilQualityGroup.JB_7_10;
   }
   if (normalized.includes('siltjord')) {
      return SoilQualityGroup.JB_7_10;
   }

   if (normalized.includes('humusjord')) {
      return SoilQualityGroup.JB_11;
   }

   console.warn(`Unmapped soil type: "${jordtype}"`);
   return null;
}

export function calculateSoilSpecificAgriculturalPrices(
   properties: JPBReferenceProperty[],
   delta?: number,
   soilFactorsByGroup?: JPBSoilFactorsByGroup,
   averageCalculationMethod: AverageCalculationMethod = AverageCalculationMethod.WeightedByArea
): { [soilGroup in SoilQualityGroup]?: number } | null {
   const resolvedAssumptions = getResolvedJPBAssumptions();
   const resolvedDelta = delta ?? getNonAgricultureToAgricultureRatio(resolvedAssumptions);
   const resolvedSoilFactors = soilFactorsByGroup ?? getSoilQualityFactorsByGroup(resolvedAssumptions);

   if (!properties || properties.length === 0) {
      return null;
   }

   const agriculturalPrices: { [propertyId: string]: number } = {};
   const agriculturalAreas: { [propertyId: string]: number } = {};
   const soilCompositionIndices: { [propertyId: string]: number } = {};

   for (const prop of properties) {
      const alpha = (prop.overlapArea?.markOverlap ?? 0) / 100;
      const P_total_per_m2 = prop.pricePerSquareMeter || 0;
      const A_total = prop.area || 0;

      if (alpha <= 0 || P_total_per_m2 <= 0 || A_total <= 0) {
         continue;
      }

      const denom = alpha + (1 - alpha) * resolvedDelta;
      if (denom <= 0) {
         continue;
      }

      const P_agri = P_total_per_m2 / denom;
      const A_agri = A_total * alpha;

      agriculturalPrices[prop.documentID] = P_agri;
      agriculturalAreas[prop.documentID] = A_agri;

      const jordbundskortOverlap = prop.overlapArea?.jordbundskortOverlap;
      if (!jordbundskortOverlap) {
         continue;
      }

      const totalPercentage = Object.values(jordbundskortOverlap).reduce(
         (sum, p) => sum + (typeof p === 'number' && p > 0 ? p : 0),
         0
      );

      const isFarmlandBased = Math.abs(totalPercentage - 100) < 10;
      const normalizationFactor = totalPercentage > 0 && !isFarmlandBased
         ? 100 / totalPercentage
         : 1.0;

      let Q_p = 0;
      for (const [soilType, percentage] of Object.entries(jordbundskortOverlap)) {
         if (typeof percentage !== 'number' || percentage <= 0) {
            continue;
         }

         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup || !SOIL_QUALITY_GROUPS.includes(soilGroup)) {
            continue;
         }

         const normalizedPercentage = percentage * normalizationFactor;
         const s_p_i = normalizedPercentage / 100;

         const q_i = resolvedSoilFactors[soilGroup];
         Q_p += s_p_i * q_i;
      }

      if (Q_p > 0) {
         soilCompositionIndices[prop.documentID] = Q_p;
      }
   }

   if (Object.keys(agriculturalPrices).length === 0 || Object.keys(soilCompositionIndices).length === 0) {
      return null;
   }

   let numerator = 0;
   let denominator = 0;

   for (const propId of Object.keys(agriculturalPrices)) {
      const P_agri = agriculturalPrices[propId];
      const A_agri = agriculturalAreas[propId];
      const Q_p = soilCompositionIndices[propId];

      if (!P_agri || !A_agri || !Q_p) {
         continue;
      }

      const weight = getWeightForAverageMode(averageCalculationMethod, A_agri);
      const contribution = weight * P_agri;
      numerator += contribution;
      denominator += weight * Q_p;
   }

   if (denominator === 0) {
      return null;
   }

   const P_base = numerator / denominator;

   const soilPrices: { [soilGroup in SoilQualityGroup]?: number } = {};

   for (const group of SOIL_QUALITY_GROUPS) {
      const q_i = resolvedSoilFactors[group];
      const P_i = P_base * q_i;
      soilPrices[group] = P_i;
   }

   return soilPrices;
}

export function calculateAreaPrice(
   agriFraction: number,
   soilComposition: { [soilGroup: string]: number },
   soilPrices: { [soilGroup: string]: number },
   delta?: number
): number {
   const resolvedDelta = delta ?? getNonAgricultureToAgricultureRatio();
   if (agriFraction <= 0) return 0;
   const totalSoilFraction = Object.values(soilComposition).reduce((a, b) => a + b, 0);
   if (totalSoilFraction <= 0) {
      throw new Error("Soil composition fractions must sum to > 0");
   }

   let P_agri = 0;
   for (const [soilGroup, fraction] of Object.entries(soilComposition)) {
      const price = soilPrices[soilGroup];
      if (price === undefined) {
         throw new Error(`Missing soil price for group ${soilGroup}`);
      }
      P_agri += fraction * price;
   }

   const P_total = P_agri * (agriFraction + (1 - agriFraction) * resolvedDelta);

   return P_total;
}

export function calculatePropertyEstimatedPrices(
   properties: JPBReferenceProperty[],
   soilPrices: { [soilGroup in SoilQualityGroup]?: number },
   delta?: number
): Array<JPBReferenceProperty & { estimatedPricePerSquareMeter: number | null }> {
   return properties.map(prop => {
      const agriFraction = (prop.overlapArea?.markOverlap ?? 0) / 100;
      const jordbundskortOverlap = prop.overlapArea?.jordbundskortOverlap;

      if (agriFraction <= 0 || !jordbundskortOverlap) {
         return { ...prop, estimatedPricePerSquareMeter: null };
      }

      const soilComposition: { [soilGroup: string]: number } = {};
      let totalMappedPercentage = 0;

      for (const [soilType, percentage] of Object.entries(jordbundskortOverlap)) {
         if (typeof percentage !== 'number' || percentage <= 0) {
            continue;
         }

         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup || !SOIL_QUALITY_GROUPS.includes(soilGroup)) {
            continue;
         }

         const fraction = percentage / 100;
         if (soilComposition[soilGroup]) {
            soilComposition[soilGroup] += fraction;
         } else {
            soilComposition[soilGroup] = fraction;
         }
         totalMappedPercentage += percentage;
      }

      if (totalMappedPercentage > 0) {
         const normalizationFactor = 100 / totalMappedPercentage;
         for (const group in soilComposition) {
            soilComposition[group] *= normalizationFactor;
         }
      }

      const hasAllPrices = Object.keys(soilComposition).every(
         group => soilPrices[group as SoilQualityGroup] !== undefined
      );

      if (!hasAllPrices || Object.keys(soilComposition).length === 0) {
         return { ...prop, estimatedPricePerSquareMeter: null };
      }

      try {
         const estimatedPrice = calculateAreaPrice(
            agriFraction,
            soilComposition,
            soilPrices as { [soilGroup: string]: number },
            delta
         );
         return { ...prop, estimatedPricePerSquareMeter: estimatedPrice };
      } catch (error) {
         console.warn(`Failed to calculate estimated price for property ${prop.documentID}:`, error);
         return { ...prop, estimatedPricePerSquareMeter: null };
      }
   });
}

export function getComparator(columnKey: string): ((a: JPBReferenceProperty, b: JPBReferenceProperty) => number) {
   switch (columnKey) {
      case 'documentID':
         return (a, b) => a.documentID.localeCompare(b.documentID);
      case 'area':
         return (a, b) => (a.area || 0) - (b.area || 0);
      case 'originalTotalPrice':
         return (a, b) => (a.originalTotalPrice || 0) - (b.originalTotalPrice || 0);
      case 'totalPrice':
         return (a, b) => (a.totalPrice || 0) - (b.totalPrice || 0);
      case 'pricePerSquareMeter':
         return (a, b) => (a.pricePerSquareMeter || 0) - (b.pricePerSquareMeter || 0);
      case 'transactionDate':
         return (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime();
      case 'transactionType':
      case 'transactionStatus':
      case 'reasonCode':
      case 'reasonText':
      case 'bfe_nr':
      case 'matchedBfeNr':
      case 'landbrugsnotering':
         return (a, b) => {
            const key = columnKey as keyof JPBReferenceProperty;
            return String(a[key] ?? "").localeCompare(String(b[key] ?? ""));
         };
      case 'error':
         return (a, b) => (a.error ?? 0) - (b.error ?? 0);
      case 'markOverlap':
         return (a, b) => {
            const aValue = a.overlapArea?.markOverlap ?? null;
            const bValue = b.overlapArea?.markOverlap ?? null;
            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;
            return aValue - bValue;
         };
      case 'markOverlapLayerYear':
         return (a, b) => (a.overlapArea?.markOverlapLayerYear ?? 0) - (b.overlapArea?.markOverlapLayerYear ?? 0);
      case 'markOverlapCalculatedAt':
         return (a, b) => (a.overlapArea?.markOverlapCalculatedAt ?? 0) - (b.overlapArea?.markOverlapCalculatedAt ?? 0);
      case 'jordbundskortCalculatedAt':
         return (a, b) => (a.overlapArea?.jordbundskortCalculatedAt ?? 0) - (b.overlapArea?.jordbundskortCalculatedAt ?? 0);
      case 'effectiveMarkPrice':
         return (a, b) => {
            const aValue = calculateEffectiveMarkSquareMeterPrice(a);
            const bValue = calculateEffectiveMarkSquareMeterPrice(b);
            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;
            return aValue - bValue;
         };
      default:
         return () => 0;
   }
}


export function getCheckedItemsFromCurrentAssumptions(
   properties: JPBReferenceProperty[],
   areaGrouping: AreaGrouping,
   yearGrouping: YearGrouping,
   customAssumptions: Partial<JPBAssumptions> | undefined,
   priceMode: PriceMode
): { checkedItems: Set<string>, assumptions: JPBAssumptions } {
   const assumptions = getResolvedJPBAssumptions(customAssumptions);

   const checkedItems = new Set(
      properties
         .filter(prop => {
            const markOverlap = prop.overlapArea?.markOverlap ?? 0;
            const hasBuildingValue =
               (typeof prop.prisDriftsbygning === "number" && Number.isFinite(prop.prisDriftsbygning) && prop.prisDriftsbygning > 0) ||
               (typeof prop.prisStuehus === "number" && Number.isFinite(prop.prisStuehus) && prop.prisStuehus > 0);
            const thresholdM2 = assumptions.buildingAreaOkM2 ?? 0;
            const buildingAreaM2 = assumptions.buildingAreaOkUseAgriculturalArea
               ? prop.area * (markOverlap / 100)
               : prop.area;
            const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || buildingAreaM2 >= thresholdM2;

            const pricePerSquareMeterToCheck =
               priceMode === PriceMode.Landbrug
                  ? calculateEffectiveMarkSquareMeterPrice(prop)
                  : prop.pricePerSquareMeter;

            const hasValidPrice =
               typeof pricePerSquareMeterToCheck === "number" &&
               Number.isFinite(pricePerSquareMeterToCheck);
            return !(
               (areaGrouping === AreaGrouping.Under30Hektar && prop.area >= 300000) ||
               (areaGrouping === AreaGrouping.Over30Hektar && prop.area < 300000) ||
               (!hasValidPrice) ||
               (pricePerSquareMeterToCheck < assumptions.pricePerMeterMin) ||
               (pricePerSquareMeterToCheck > assumptions.pricePerMeterMax) ||
               (prop.area < assumptions.areaMin) ||
               (prop.area > assumptions.areaMax) ||
               (!assumptions.includeTransactionTypes.includes(prop.transactionType)) ||
               (!buildingAreaOk) ||
               (markOverlap <= assumptions.effectiveMarkOverlapThreshold) ||
               (assumptions.excludeBuildings && hasBuildingValue)
            );
         })
         .map(prop => prop.documentID)
   );
   return { checkedItems, assumptions };
}
